import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Minus, Plus, ShoppingBag, Check, ChevronLeft, Truck, Shield, RotateCcw, Clock } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useCart } from '../context/CartContext'
import Button from '../components/ui/Button'
import ProductCard from '../components/product/ProductCard'
import SEO from '../components/SEO'
import WaitlistForm from '../components/product/WaitlistForm'

const benefits = [
  { icon: Truck, text: 'Free shipping over $75' },
  { icon: Shield, text: '30-day guarantee' },
  { icon: RotateCcw, text: 'Easy returns' },
]

export default function Product() {
  const { slug } = useParams()
  const { addToCart } = useCart()
  const [product, setProduct] = useState(null)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [quantity, setQuantity] = useState(1)
  const [addedToCart, setAddedToCart] = useState(false)
  const [activeImage, setActiveImage] = useState(0)

  useEffect(() => {
    fetchProduct()
  }, [slug])

  const fetchProduct = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .single()

      if (error) throw error

      setProduct(data)

      // Fetch related products
      if (data) {
        const { data: related } = await supabase
          .from('products')
          .select('*')
          .eq('category', data.category)
          .neq('id', data.id)
          .eq('in_stock', true)
          .limit(3)

        setRelatedProducts(related || [])
      }
    } catch (error) {
      console.error('Error fetching product:', error)
      setProduct(null)
    } finally {
      setIsLoading(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const handleAddToCart = () => {
    addToCart(product, quantity)
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading product...</div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-display-md text-pp-ink mb-4">
            Product Not Found
          </h1>
          <p className="text-pp-stone mb-6">
            Sorry, we could not find the product you are looking for.
          </p>
          <Button as={Link} to="/shop">
            Back to Shop
          </Button>
        </div>
      </div>
    )
  }

  const images = product.images || []
  const features = product.features || []
  const developmentalBenefits = product.developmental_benefits || []
  const comingSoon = product.coming_soon || product.comingSoon

  return (
    <div className="min-h-screen bg-pp-ivory">
      <SEO
        title={product.name}
        description={product.short_description || product.description?.slice(0, 160)}
        image={images[0]}
        url={`/product/${product.slug}`}
        type="product"
      />
      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        {/* Product Section */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="aspect-square bg-pp-sand/30 rounded-2xl overflow-hidden relative">
              {images[activeImage] ? (
                <img
                  src={images[activeImage]}
                  alt={`${product.name} - Image ${activeImage + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className={`absolute inset-0 flex items-center justify-center bg-pp-sand/30 ${images[activeImage] ? 'hidden' : ''}`}
              >
                <span className="text-pp-stone">No Image Available</span>
              </div>
            </div>
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      activeImage === index
                        ? 'border-pp-terracotta'
                        : 'border-transparent hover:border-pp-sand'
                    }`}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={`${product.name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.parentElement.innerHTML = `<div class="w-full h-full bg-pp-sand/30 flex items-center justify-center"><span class="text-pp-stone text-xs">${index + 1}</span></div>`
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-pp-sand/30 flex items-center justify-center">
                        <span className="text-pp-stone text-xs">{index + 1}</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:py-4"
          >
            {/* Badges */}
            <div className="flex gap-2 mb-4">
              {comingSoon && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-pp-charcoal text-white text-xs font-medium rounded-full">
                  <Clock className="w-3 h-3" />
                  Coming Soon
                </span>
              )}
              {product.bestseller && !comingSoon && (
                <span className="inline-block px-3 py-1 bg-pp-terracotta text-white text-xs font-medium rounded-full">
                  Bestseller
                </span>
              )}
              {product.compare_price && !comingSoon && (
                <span className="inline-block px-3 py-1 bg-pp-sage text-white text-xs font-medium rounded-full">
                  Sale
                </span>
              )}
            </div>

            <h1 className="font-display text-display-md text-pp-ink mb-2">
              {product.name}
            </h1>

            <p className="text-pp-stone mb-4">{product.age_range}</p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-display text-2xl text-pp-ink">
                {formatPrice(product.price)}
              </span>
              {product.compare_price && (
                <span className="text-pp-stone line-through">
                  {formatPrice(product.compare_price)}
                </span>
              )}
            </div>

            <p className="text-pp-stone leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Quantity & Add to Cart - or Waitlist for Coming Soon */}
            {comingSoon ? (
              <div className="mb-8 p-6 bg-pp-linen rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-pp-charcoal" />
                  <h3 className="font-display text-lg text-pp-ink">Coming Soon</h3>
                </div>
                <WaitlistForm productId={product.id} />
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="flex items-center border border-pp-sand rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-pp-charcoal hover:text-pp-terracotta transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 font-medium text-pp-ink">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 text-pp-charcoal hover:text-pp-terracotta transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <Button
                  onClick={handleAddToCart}
                  className="flex-1"
                  disabled={!product.in_stock}
                >
                  {addedToCart ? (
                    <>
                      <Check className="w-5 h-5" />
                      Added to Cart
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5" />
                      {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Benefits */}
            <div className="flex flex-wrap gap-6 pb-8 border-b border-pp-sand">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-2 text-sm text-pp-stone">
                  <benefit.icon className="w-4 h-4" />
                  {benefit.text}
                </div>
              ))}
            </div>

            {/* Features */}
            {features.length > 0 && (
              <div className="py-8 border-b border-pp-sand">
                <h3 className="font-display text-lg text-pp-ink mb-4">Features</h3>
                <ul className="space-y-2">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-pp-stone text-sm">
                      <Check className="w-4 h-4 text-pp-sage mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Developmental Benefits */}
            {developmentalBenefits.length > 0 && (
              <div className="py-8">
                <h3 className="font-display text-lg text-pp-ink mb-4">
                  Developmental Benefits
                </h3>
                <ul className="space-y-2">
                  {developmentalBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2 text-pp-stone text-sm">
                      <Check className="w-4 h-4 text-pp-terracotta mt-0.5 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16 lg:mt-24 pt-12 border-t border-pp-sand">
            <h2 className="font-display text-display-sm text-pp-ink mb-8">
              You May Also Like
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
